import React, { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import { BarcodeStatus, MasterBarcodeEntry, MasterCropData, MasterUPCData, SectionTarget } from '../../types';
import { CropThumbnail } from './CropThumbnail';
import { ActionMode } from '../../hooks/useUPCActions';
import { getCachedImageUrl, ZoomMeta } from '../../hooks/useZoomPreview';
import { normalizeBarcode } from '../../utils/normalizeBarcode';

// ── Types ──

export interface FocusedHighlightInfo {
  type: string;
  cropId?: string;
  cropIds?: string[];
  cropItemNumber?: string;
}

export interface UPCColumnViewProps {
  upc: MasterUPCData;
  // Selection
  selectedCropIds: Set<string>;
  onCropClick: (cropId: string, shiftKey: boolean) => void;
  onSectionClick: (target: SectionTarget) => void;
  // Mode
  mode: ActionMode;
  mergeSourceVariantId: string | null;
  activeVariantId: string | null;
  onActiveVariantChange: (id: string | null) => void;
  // Variant colors
  variantColorMap: Map<string, string>;
  // Highlight (optional — only used by Variant Review)
  focusedHighlight?: FocusedHighlightInfo | null;
  isSectionHighlighted?: (sectionKey: string, target: SectionTarget, count?: number) => boolean;
  // Zoom
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
  // Optional: remove button for Master Label
  onRemoveUpc?: (upc: string) => void;
  // Show crop type triangles (demo/task player only)
  showTriangles?: boolean;
  // Recently moved crops (for glow animation)
  recentlyMovedCropIds?: Set<string>;
  // Custom crop thumbnail size (also drives column width)
  cropSize?: number;
  // Retry token for crop image reloads
  reloadVersion?: number;
  // Preload a group of image URLs on first hover
  onPreloadGroup?: (urls: string[]) => void;
  highlightedCropIds?: Set<string>;
  highlightColor?: string;
  highlightGlow?: string;
}

// ── Component ──

export const UPCColumnView: React.FC<UPCColumnViewProps> = ({
  upc,
  selectedCropIds,
  onCropClick,
  onSectionClick,
  mode,
  mergeSourceVariantId,
  activeVariantId,
  onActiveVariantChange,
  variantColorMap,
  focusedHighlight,
  isSectionHighlighted,
  onImageHover,
  onImageMove,
  onImageLeave,
  onRemoveUpc,
  showTriangles = false,
  recentlyMovedCropIds,
  cropSize,
  reloadVersion = 0,
  onPreloadGroup,
  highlightedCropIds,
  highlightColor,
  highlightGlow,
}) => {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const preloadedGroups = React.useRef(new Set<string>());
  const HEADER_SIDE_WIDTH = 22;
  const HEADER_COUNT_WIDTH = 28;
  const UPC_NUMBER_COLORS = [
    { background: 'rgba(88, 166, 255, 0.9)', border: '1px solid rgba(145, 193, 255, 0.6)', color: '#f3f9ff' },
    { background: 'rgba(248, 81, 73, 0.92)', border: '1px solid rgba(255, 160, 153, 0.6)', color: '#fff5f5' },
    { background: 'rgba(163, 113, 247, 0.92)', border: '1px solid rgba(204, 177, 255, 0.6)', color: '#fbf8ff' },
    { background: 'rgba(100, 116, 139, 0.92)', border: '1px solid rgba(179, 190, 208, 0.55)', color: '#f8fafc' },
    { background: 'rgba(236, 72, 153, 0.92)', border: '1px solid rgba(251, 182, 223, 0.55)', color: '#fff7fb' },
    { background: 'rgba(99, 102, 241, 0.92)', border: '1px solid rgba(165, 180, 252, 0.55)', color: '#f5f7ff' },
  ] as const;
  const columnId = upc.itemNumber;
  const primaryUpcEntries = upc.barcodeSummary?.primaryUpcs ?? [];
  const alternateUpcEntries = upc.barcodeSummary?.alternateUpcs ?? [];
  const caseUpcEntries = upc.barcodeSummary?.casePacks ?? [];
  const allUpcEntries = [
    ...primaryUpcEntries,
    ...alternateUpcEntries,
    ...caseUpcEntries,
  ];

  const totalCrops = upc.variants.reduce((a, v) => a + v.crops.length, 0) + (upc.pendingCrops?.length || 0) + upc.unassignedCrops.length + upc.nonFrontCrops.length + upc.cartonCrops.length + (upc.inactiveCrops?.length || 0);
  const upcCount = allUpcEntries.length;
  const numberedUpcEntries = [...primaryUpcEntries, ...alternateUpcEntries];
  const numberedUpcMetaByNormalized = new Map(
    numberedUpcEntries
      .map((entry, index) => [normalizeBarcode(entry.upc), {
        number: index + 1,
        ...UPC_NUMBER_COLORS[index % UPC_NUMBER_COLORS.length],
      }] as const)
      .filter(([normalized]) => Boolean(normalized))
      .filter(([normalized], index, entries) => entries.findIndex(([value]) => value === normalized) === index)
  );
  const knownBarcodes = new Set(numberedUpcEntries.map((entry) => normalizeBarcode(entry.upc)).filter(Boolean));
  const barcodeEntryByNormalized = new Map(
    allUpcEntries
      .map((entry) => [normalizeBarcode(entry.upc), entry] as const)
      .filter(([normalized]) => Boolean(normalized))
      .filter(([normalized], index, entries) => entries.findIndex(([value]) => value === normalized) === index)
  );

  const preloadGroup = (groupKey: string, crops: MasterCropData[]) => {
    if (!onPreloadGroup || preloadedGroups.current.has(groupKey)) return;
    preloadedGroups.current.add(groupKey);
    onPreloadGroup(crops.map(c => c.imageUrl));
  };

  // Stable sort — scan-sourced crops first, then ID order within each source bucket.
  const sorted = (crops: MasterCropData[]) => [...crops].sort((a, b) => {
    const aScan = a.source === 'scan' ? 0 : 1;
    const bScan = b.source === 'scan' ? 0 : 1;
    if (aScan !== bScan) return aScan - bScan;
    return a.id.localeCompare(b.id);
  });
  const cropNeedsBarcodeWarning = (crop: MasterCropData) => (
    crop.source === 'scan'
      && crop.classificationType !== 'carton'
      && knownBarcodes.size > 0
      && !knownBarcodes.has(normalizeBarcode(crop.barcode))
  );
  const getCropBadgeConfig = (crop: MasterCropData, sectionType: 'front' | 'non-front' | 'carton') => {
    if (crop.source !== 'scan') {
      return { leftMode: null, rightMode: null } as const;
    }

    const isCarton = sectionType === 'carton' || crop.classificationType === 'carton';
    if (isCarton) {
      return {
        leftMode: 'scan' as const,
        rightMode: 'carton' as const,
      };
    }

    const matchedBadge = numberedUpcMetaByNormalized.get(normalizeBarcode(crop.barcode));
    if (matchedBadge) {
      return {
        leftMode: 'number' as const,
        leftLabel: String(matchedBadge.number),
        leftBackground: matchedBadge.background,
        leftBorder: matchedBadge.border,
        leftColor: matchedBadge.color,
        rightMode: null,
      };
    }

    return {
      leftMode: 'scan' as const,
      rightMode: cropNeedsBarcodeWarning(crop) ? ('warning' as const) : null,
    };
  };
  const buildCropZoomMeta = (crop: MasterCropData): ZoomMeta => ({
    id: crop.id,
    source: crop.source,
    barcode: crop.barcode,
    normalizedUpc: crop.normalizedUpc,
    classificationType: crop.classificationType,
    warningMessage: cropNeedsBarcodeWarning(crop) ? 'Barcode does not match known barcodes' : undefined,
    imageResolution: crop.imageResolution,
    createdBy: crop.createdBy,
    createdAt: crop.createdAt,
    updatedBy: crop.updatedBy,
    updatedAt: crop.updatedAt,
  });
  function getEntryImages(entry: MasterBarcodeEntry) {
    const seen = new Set<string>();
    return entry.storeDetails
      .flatMap((detail) => detail.images)
      .filter((image) => {
        const key = `${image.url}::${image.sourceLabel || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }
  function getEntrySharedDisplay(entry: MasterBarcodeEntry) {
    const details = entry.storeDetails;
    if (details.length === 0) return null;
    const signatures = details.map((detail) => JSON.stringify({
      name: detail.name || '',
      casePack: detail.casePack ?? null,
      images: detail.images.map((image) => image.url),
    }));
    if (signatures.every((signature) => signature === signatures[0])) {
      return details[0];
    }
    return null;
  }
  const getVariantRepresentativeCrop = (variant: typeof upc.variants[number]) => (
    variant.crops.find((crop) => crop.id === variant.representativeCropId) || variant.crops[0]
  );
  const getVariantBarcodeInfo = (variant: typeof upc.variants[number]) => {
    const representativeCrop = getVariantRepresentativeCrop(variant);
    const scanCrops = sorted(variant.crops.filter((crop) => crop.source === 'scan'));
    const barcodeCandidates = [
      { value: variant.verifiedBarcode, priority: 1 },
      { value: representativeCrop?.source === 'scan' ? representativeCrop.barcode : undefined, priority: 2 },
      { value: scanCrops.find((crop) => normalizeBarcode(crop.barcode))?.barcode, priority: 3 },
    ];
    const selected = barcodeCandidates.find((candidate) => normalizeBarcode(candidate.value));
    const normalizedBarcode = normalizeBarcode(selected?.value);
    const matchedEntry = normalizedBarcode ? barcodeEntryByNormalized.get(normalizedBarcode) : undefined;
    const sharedDisplay = matchedEntry ? getEntrySharedDisplay(matchedEntry) : null;
    const fallbackDetail = matchedEntry?.storeDetails[0];
    const displayDetail = sharedDisplay ?? fallbackDetail;
    const productLabel = displayDetail?.name
      ? `${displayDetail.name}${displayDetail.casePack ? ` (${displayDetail.casePack})` : ''}`
      : undefined;

    return {
      barcode: selected?.value,
      normalizedBarcode,
      priority: selected?.priority ?? 99,
      productLabel,
    };
  };
  const compareVariants = (left: typeof upc.variants[number], right: typeof upc.variants[number]) => {
    const leftInfo = getVariantBarcodeInfo(left);
    const rightInfo = getVariantBarcodeInfo(right);
    const leftHasBarcode = leftInfo.normalizedBarcode ? 0 : 1;
    const rightHasBarcode = rightInfo.normalizedBarcode ? 0 : 1;
    if (leftHasBarcode !== rightHasBarcode) return leftHasBarcode - rightHasBarcode;
    if (leftInfo.normalizedBarcode !== rightInfo.normalizedBarcode) {
      return (leftInfo.normalizedBarcode || '').localeCompare(rightInfo.normalizedBarcode || '');
    }
    if (leftInfo.priority !== rightInfo.priority) return leftInfo.priority - rightInfo.priority;
    return left.id.localeCompare(right.id);
  };
  const sortedVariants = [...upc.variants].sort(compareVariants);
  const renderUpcNumberBadge = (normalizedBarcode: string) => {
    const badge = numberedUpcMetaByNormalized.get(normalizedBarcode);
    if (!badge) return null;
    return (
      <div
        style={{
          minWidth: 16,
          height: 16,
          padding: '0 4px',
          borderRadius: 999,
          background: badge.background,
          border: badge.border,
          color: badge.color,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: 8,
          fontWeight: 700,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {badge.number}
      </div>
    );
  };
  const renderCropCornerBadge = (
    side: 'left' | 'right',
    options: {
      mode: 'number' | 'scan' | 'warning' | 'carton';
      label?: string;
      background?: string;
      border?: string;
      color?: string;
    }
  ) => (
    <div
      style={{
        position: 'absolute',
        [side]: 2,
        bottom: 2,
        pointerEvents: 'none',
        minWidth: 8,
        height: 8,
        padding: '0 2px',
        borderRadius: 999,
        background: options.mode === 'number'
          ? (options.background || 'rgba(88, 166, 255, 0.92)')
          : options.mode === 'warning'
            ? 'rgba(210, 153, 34, 0.96)'
            : options.mode === 'carton'
              ? 'rgba(232, 151, 108, 0.96)'
              : 'rgba(63, 185, 80, 0.92)',
        border: options.mode === 'number'
          ? (options.border || '1px solid rgba(145, 193, 255, 0.55)')
          : options.mode === 'warning'
            ? '1px solid rgba(255, 220, 120, 0.6)'
            : options.mode === 'carton'
              ? '1px solid rgba(255, 202, 166, 0.55)'
              : '1px solid rgba(164, 239, 176, 0.55)',
        color: options.mode === 'number'
          ? (options.color || '#f3f9ff')
          : options.mode === 'warning'
            ? '#1a1a1a'
            : options.mode === 'carton'
              ? '#fff7f2'
              : '#f3fff5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {options.mode === 'warning' ? (
        <WarningAmberOutlinedIcon style={{ fontSize: 6 }} />
      ) : (
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 6,
          fontWeight: 700,
          lineHeight: 1,
        }}>
          {options.mode === 'number' ? options.label : options.mode === 'carton' ? 'C' : 'S'}
        </span>
      )}
    </div>
  );
  const renderBarcodeImages = (images: { url: string; sourceLabel?: string }[], imageSize = 28) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {images.map((image) => (
        <div
          key={`${image.url}-${image.sourceLabel || 'image'}`}
          title={image.sourceLabel || 'image'}
          style={{
            width: imageSize,
            height: imageSize,
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid var(--border)',
            background: '#000',
            flexShrink: 0,
          }}
          onMouseEnter={onImageHover ? (e) => onImageHover(e, image.url, image.sourceLabel ? { source: image.sourceLabel } : undefined) : undefined}
          onMouseMove={onImageMove ? (e) => onImageMove(e, image.url, image.sourceLabel ? { source: image.sourceLabel } : undefined) : undefined}
          onMouseLeave={onImageLeave}
        >
          <img
            src={getCachedImageUrl(image.url)}
            alt={image.sourceLabel || 'barcode image'}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
            draggable={false}
          />
        </div>
      ))}
    </div>
  );
  const renderVariantMediaSlot = (
    key: string,
    options: {
      imageUrl?: string;
      alt: string;
      label?: string;
      icon?: React.ReactNode;
      title: string;
      size?: number;
      meta?: ZoomMeta;
    }
  ) => {
    const size = options.size ?? 20;
    const imageUrl = options.imageUrl;
    return (
      <div
        key={key}
        title={options.title}
        style={{
          width: size,
          height: size,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid var(--border)',
          background: options.imageUrl ? '#000' : 'rgba(255,255,255,0.03)',
          color: 'var(--text-tertiary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        onMouseEnter={imageUrl && onImageHover ? (e) => onImageHover(e, imageUrl, options.meta) : undefined}
        onMouseMove={imageUrl && onImageMove ? (e) => onImageMove(e, imageUrl, options.meta) : undefined}
        onMouseLeave={imageUrl ? onImageLeave : undefined}
      >
        {options.imageUrl ? (
          <img
            src={getCachedImageUrl(options.imageUrl)}
            alt={options.alt}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
            draggable={false}
          />
        ) : options.icon ? (
          options.icon
        ) : (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 8,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            lineHeight: 1,
          }}>
            {options.label}
          </span>
        )}
      </div>
    );
  };
  // ── Render helpers ──

  const renderCrop = (crop: MasterCropData, sectionType: 'front' | 'non-front' | 'carton', variantId?: string, isRepresentative = false, isPending = false) => {
    const isFocusedCrop = !!(focusedHighlight && (
      focusedHighlight.cropId === crop.id ||
      (focusedHighlight.cropIds && focusedHighlight.cropIds.includes(crop.id))
    ));
    const isLinkHighlighted = highlightedCropIds?.has(crop.id) ?? false;
    const badgeConfig = getCropBadgeConfig(crop, sectionType);
    return (
      <CropThumbnail
        key={crop.id}
        crop={crop}
        sectionType={sectionType}
        size={cropSize}
        isSelected={selectedCropIds.has(crop.id)}
        isRepresentative={isRepresentative}
        isFocused={isFocusedCrop}
        isLinkHighlighted={isLinkHighlighted}
        highlightColor={highlightColor}
        highlightGlow={highlightGlow}
        isPending={isPending}
        showTriangle={showTriangles}
        isRecentlyMoved={recentlyMovedCropIds?.has(crop.id) ?? false}
        reloadVersion={reloadVersion}
        leftBadgeMode={badgeConfig.leftMode}
        leftBadgeLabel={badgeConfig.leftLabel}
        leftBadgeBackground={badgeConfig.leftBackground}
        leftBadgeBorder={badgeConfig.leftBorder}
        leftBadgeColor={badgeConfig.leftColor}
        rightBadgeMode={badgeConfig.rightMode}
        onClick={(e) => { e.stopPropagation(); onCropClick(crop.id, e.shiftKey); }}
        onImageHover={onImageHover}
        onImageMove={onImageMove}
        onImageLeave={onImageLeave}
      />
    );
  };

  const renderSectionHeader = (
    label: string,
    count: number,
    sectionKey: string,
    target: SectionTarget,
    options?: { color?: string; isMergeSource?: boolean; barcodeStatus?: BarcodeStatus }
  ) => {
    const isCollapsed = collapsedSections.has(sectionKey);
    const isExplicitVariantTarget = target.type !== 'variant' || !!target.variantId;
    const isDropTarget =
      (mode === 'crops-selected' || (mode === 'merge-source' && target.type === 'variant' && target.variantId !== mergeSourceVariantId)) &&
      target.type !== 'pending' &&
      isExplicitVariantTarget;
    const isClickable = isDropTarget || options?.isMergeSource || mode === 'idle';
    const isMergeSource = options?.isMergeSource;

    // Layout: [collapse 22px fixed] [label ... flex] [count]
    // The 22px column ensures labels always start at the same X regardless of collapse icon
    return (
      <div
        onClick={() => {
          if (isDropTarget || isMergeSource || mode === 'idle') {
            onSectionClick(target);
          }
        }}
        style={{
          display: 'flex', alignItems: 'center', padding: '4px 8px 4px 0',
          cursor: isClickable ? 'pointer' : 'default',
          borderRadius: 'var(--radius-sm)',
          borderLeft: isMergeSource ? '3px solid var(--danger)' : '3px solid transparent',
          background: isMergeSource ? 'var(--danger-subtle)' : 'transparent',
          transition: 'all 150ms ease',
          userSelect: 'none',
        }}
        onMouseEnter={e => {
          if (isDropTarget) { e.currentTarget.style.background = mode === 'merge-source' ? 'var(--positive-subtle)' : 'var(--purple-subtle)'; e.currentTarget.style.borderLeftColor = mode === 'merge-source' ? 'var(--positive)' : 'var(--purple)'; }
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = isMergeSource ? 'var(--danger-subtle)' : 'transparent';
          e.currentTarget.style.borderLeftColor = isMergeSource ? 'var(--danger)' : 'transparent';
        }}
      >
        <div style={{ width: HEADER_SIDE_WIDTH, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {count > 0 ? (
            <div onClick={(e) => { e.stopPropagation(); setCollapsedSections(prev => { const n = new Set(prev); if (n.has(sectionKey)) n.delete(sectionKey); else n.add(sectionKey); return n; }); }} style={{ cursor: 'pointer', display: 'flex' }}>
              {isCollapsed
                ? <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                : <KeyboardArrowDownIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
              }
            </div>
          ) : (
            <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'transparent' }} />
          )}
        </div>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
          color: isMergeSource ? 'var(--danger)' : 'var(--text-tertiary)',
          textTransform: 'uppercase', letterSpacing: '1px',
          flex: 1, textAlign: 'center',
        }}>{label}</span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          color: 'var(--text-tertiary)',
          flexShrink: 0,
          width: HEADER_COUNT_WIDTH,
          textAlign: 'right',
        }}>{count}</span>
        {isMergeSource && (
          <span style={{ fontSize: 8, fontWeight: 700, color: 'var(--danger)', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>
            SOURCE
          </span>
        )}
      </div>
    );
  };

  const checkHighlight = (sectionKey: string, target: SectionTarget, count?: number) => {
    return isSectionHighlighted ? isSectionHighlighted(sectionKey, target, count) : false;
  };

  // ── Main render ──

  return (
    <div style={{
      flex: 1, minWidth: 200,
      border: '1px solid var(--border)', borderRadius: 'var(--radius)',
      background: 'var(--surface)', display: 'flex', flexDirection: 'column',
      maxHeight: '100%', overflow: 'hidden',
    }}>
      {/* UPC header */}
      <div style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
        {/* Product images — left side */}
        {upc.alternativeImageUrls && upc.alternativeImageUrls.length > 0 && (
          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
            {upc.alternativeImageUrls.map((url, i) => (
              <div
                key={i}
                style={{ position: 'relative', overflow: 'hidden', borderRadius: 3, border: '1px solid var(--border)', flexShrink: 0 }}
                onMouseEnter={onImageHover ? (e) => onImageHover(e, url) : undefined}
                onMouseMove={onImageMove ? (e) => onImageMove(e, url) : undefined}
                onMouseLeave={onImageLeave}
              >
                <img src={getCachedImageUrl(url)} alt={`Alt ${i + 1}`} style={{
                  width: 36, height: 36, objectFit: 'contain', background: '#000', display: 'block',
                }} draggable={false} />
              </div>
            ))}
          </div>
        )}
        {/* Text info — right side */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {upc.productName}
              </div>
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-tertiary)', flexShrink: 0 }}>
              {totalCrops}
            </span>
            {onRemoveUpc && (
              <button
                onClick={() => onRemoveUpc(columnId)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                  color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', flexShrink: 0,
                }}
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-tertiary)' }}>
            {upc.itemNumber && (
              <span>ITEM_NUMBER <span style={{ color: 'var(--text-secondary)' }}>{upc.itemNumber}</span></span>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable sections — fixed order: Pending, Unassigned, Variants, Carton, Non-front */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* ── Group: UPCs (blue-grey) ── */}
        {allUpcEntries.length > 0 && (
          <div style={{
            borderLeft: '2px solid rgba(88, 166, 255, 0.35)', marginLeft: 4,
            background: 'rgba(88, 166, 255, 0.05)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
            paddingTop: 2, paddingBottom: 2,
          }}>
            <div style={{ padding: '0 4px' }}>
              <div
                onClick={() => {
                  setCollapsedSections(prev => {
                    const next = new Set(prev);
                    const sectionKey = `upcs-${columnId}`;
                    if (next.has(sectionKey)) next.delete(sectionKey);
                    else next.add(sectionKey);
                    return next;
                  });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '2px 8px 2px 0',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <div style={{ width: HEADER_SIDE_WIDTH, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {collapsedSections.has(`upcs-${columnId}`)
                    ? <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                    : <KeyboardArrowDownIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                  }
                </div>
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: 'var(--text-tertiary)',
                  textAlign: 'center',
                }}>
                  UPCs
                </div>
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  width: HEADER_COUNT_WIDTH,
                  textAlign: 'right',
                  flexShrink: 0,
                }}>
                  {upcCount}
                </span>
              </div>
              {!collapsedSections.has(`upcs-${columnId}`) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '2px 8px 8px 22px' }}>
                  {[
                    { key: 'primary', label: 'Primary', entries: primaryUpcEntries },
                    { key: 'alternate', label: 'Alternate', entries: alternateUpcEntries },
                    { key: 'case', label: 'Case', entries: caseUpcEntries },
                  ].filter((section) => section.entries.length > 0).map((section) => (
                    <div key={section.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 8,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'var(--text-tertiary)',
                      }}>
                        {section.label}
                      </div>
                      {section.entries.map((entry) => {
                        const normalizedEntryBarcode = normalizeBarcode(entry.upc);
                        const matchedVariants = sortedVariants.filter((variant) => (
                          getVariantBarcodeInfo(variant).normalizedBarcode === normalizedEntryBarcode
                        ));
                        const sharedDisplay = getEntrySharedDisplay(entry);
                        const entryImages = getEntryImages(entry);
                        const entryNumberBadge = section.key === 'case' ? null : renderUpcNumberBadge(normalizedEntryBarcode);
                        return (
                          <div
                            key={`${section.key}-${entry.upc}`}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 6,
                              paddingBottom: 8,
                              borderBottom: '1px solid rgba(255,255,255,0.04)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              {entryNumberBadge}
                              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {sharedDisplay ? (
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'max-content minmax(0, 1fr)',
                                      columnGap: 10,
                                      alignItems: 'center',
                                      fontFamily: 'var(--font-mono)',
                                      fontSize: 9,
                                      color: 'var(--text-secondary)',
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    <span style={{ color: 'var(--text-primary)' }}>{entry.upc}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap' }}>
                                      <span style={{ color: 'var(--text-tertiary)' }}>
                                        {entry.storeDetails.map((detail) => detail.storeLabel).join('/')}
                                      </span>
                                      <span>
                                        {sharedDisplay.name || ''}
                                        {sharedDisplay.casePack ? ` (${sharedDisplay.casePack})` : ''}
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'max-content minmax(0, 1fr)',
                                      columnGap: 10,
                                      rowGap: 3,
                                      alignItems: 'center',
                                      fontFamily: 'var(--font-mono)',
                                      fontSize: 9,
                                      color: 'var(--text-secondary)',
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    <span
                                      style={{
                                        gridRow: `1 / span ${entry.storeDetails.length}`,
                                        alignSelf: 'center',
                                        fontSize: 9,
                                        color: 'var(--text-primary)',
                                      }}
                                    >
                                      {entry.upc}
                                    </span>
                                    {entry.storeDetails.map((detail) => (
                                      <div
                                        key={`${entry.upc}-${detail.storeLabel}`}
                                        style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap' }}
                                      >
                                        <span style={{ color: 'var(--text-tertiary)' }}>{detail.storeLabel}</span>
                                        <span>
                                          {detail.name || ''}
                                          {detail.casePack ? ` (${detail.casePack})` : ''}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {entryImages.length > 0 && renderBarcodeImages(entryImages, 26)}
                            </div>

                            {matchedVariants.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {matchedVariants.map((variant) => {
                                  const repCrop = variant.crops.find((crop) => crop.id === variant.representativeCropId) || variant.crops[0];
                                  const isVariantVerified = variant.barcodeStatus === 'confirmed' || Boolean(variant.verifiedBarcode);
                                  return (
                                    <div
                                      key={`${section.key}-variant-${entry.upc}-${variant.id}`}
                                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {renderVariantMediaSlot(`front-${section.key}-${variant.id}`, {
                                          imageUrl: isVariantVerified ? variant.frontImageUrl : undefined,
                                          alt: 'Front',
                                          label: 'F',
                                          title: isVariantVerified && variant.frontImageUrl ? 'Front image' : 'No front image',
                                          meta: isVariantVerified && variant.frontImageUrl ? { source: 'variant front' } : undefined,
                                        })}
                                        {renderVariantMediaSlot(`back-${section.key}-${variant.id}`, {
                                          imageUrl: isVariantVerified ? variant.backImageUrl : undefined,
                                          alt: 'Back',
                                          label: 'B',
                                          title: isVariantVerified && variant.backImageUrl ? 'Back image' : 'No back image',
                                          meta: isVariantVerified && variant.backImageUrl ? { source: 'variant back' } : undefined,
                                        })}
                                        {renderVariantMediaSlot(`barcode-${section.key}-${variant.id}`, {
                                          imageUrl: isVariantVerified ? variant.barcodeImageUrl : undefined,
                                          alt: 'Barcode',
                                          icon: <QrCode2OutlinedIcon style={{ fontSize: 11, color: 'var(--text-tertiary)' }} />,
                                          title: isVariantVerified && variant.barcodeImageUrl ? 'Barcode image' : 'No barcode image',
                                          meta: isVariantVerified && variant.barcodeImageUrl ? { source: 'variant barcode' } : undefined,
                                        })}
                                        {renderVariantMediaSlot(`rep-${section.key}-${variant.id}`, {
                                          imageUrl: repCrop?.imageUrl,
                                          alt: 'Representative',
                                          icon: <StarIcon style={{ fontSize: 10, color: 'var(--text-tertiary)', opacity: 0.7 }} />,
                                          title: repCrop ? 'Representative image' : 'No representative image',
                                          meta: repCrop ? buildCropZoomMeta(repCrop) : undefined,
                                        })}
                                      </div>
                                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--text-tertiary)' }}>
                                        {variant.id}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Group: Pending (purple) ── */}
        <div style={{
          borderLeft: '2px solid rgba(163,113,247,0.35)', marginLeft: 4,
          background: 'rgba(163,113,247,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {(() => {
          const pending = upc.pendingCrops || [];
          const pendingSectionKey = `pending-${columnId}`;
          const pendingTarget: SectionTarget = { itemNumber: columnId, type: 'pending' };
          const isPendingHighlighted = checkHighlight(pendingSectionKey, pendingTarget, pending.length);
          return (
            <div style={{
              padding: '0 4px', marginBottom: 2,
              borderRadius: 'var(--radius-sm)',
              background: isPendingHighlighted ? 'var(--purple-subtle)' : 'transparent',
              boxShadow: isPendingHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
              transition: 'all 150ms ease',
            }}>
              {renderSectionHeader('Pending', pending.length, pendingSectionKey, pendingTarget)}
              {pending.length > 0 && !collapsedSections.has(pendingSectionKey) && (
                <div onMouseEnter={() => preloadGroup('pending-' + columnId, pending)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 8px 4px 0' }}>
                  {sorted(pending).map(crop => renderCrop(crop, 'front', undefined, false, true))}
                </div>
              )}
            </div>
          );
        })()}
        </div>

        {/* ── Group: Unassigned (grey) ── */}
        <div style={{
          borderLeft: '2px solid rgba(139,148,158,0.35)', marginLeft: 4,
          background: 'rgba(139,148,158,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {(() => {
          const unassignedSectionKey = `novar-${columnId}`;
          const unassignedTarget: SectionTarget = { itemNumber: columnId, type: 'no-variant' };
          const isUnassignedHighlighted = checkHighlight(unassignedSectionKey, unassignedTarget, upc.unassignedCrops.length);
          return (
            <div style={{
              padding: '0 4px', marginBottom: 2,
              borderRadius: 'var(--radius-sm)',
              background: isUnassignedHighlighted ? 'var(--purple-subtle)' : 'transparent',
              boxShadow: isUnassignedHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
              transition: 'all 150ms ease',
            }}>
              {renderSectionHeader('Unassigned', upc.unassignedCrops.length, unassignedSectionKey, unassignedTarget)}
              {upc.unassignedCrops.length > 0 && !collapsedSections.has(unassignedSectionKey) && (
                <div onMouseEnter={() => preloadGroup('unassigned-' + columnId, upc.unassignedCrops)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 8px 4px 0' }}>
                  {sorted(upc.unassignedCrops).map(crop => renderCrop(crop, 'front'))}
                </div>
              )}
            </div>
          );
        })()}
        </div>

        {/* ── Group: Variants (green) ── */}
        <div style={{
          borderLeft: '2px solid rgba(63,185,80,0.35)', marginLeft: 4,
          background: 'rgba(63,185,80,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        <div style={{ padding: '0 4px' }}>
          {renderSectionHeader('Variants', upc.variants.length, `variants-${columnId}`, { itemNumber: columnId, type: 'variant' })}
        </div>
        {!collapsedSections.has(`variants-${columnId}`) && sortedVariants.map((variant) => {
          const sectionKey = `variant-${variant.id}`;
          const isMergeSource = mergeSourceVariantId === variant.id;
          const variantTarget: SectionTarget = { itemNumber: columnId, type: 'variant', variantId: variant.id };
          const isVariantHighlighted = checkHighlight(sectionKey, variantTarget);

          const isConfirmed = variant.barcodeStatus === 'confirmed';
          const isBarcPending = variant.barcodeStatus === 'pending';
          const variantBarcodeInfo = getVariantBarcodeInfo(variant);
          const showReliableVariantLabel = Boolean(variantBarcodeInfo.normalizedBarcode);
          const repCrop = variant.crops.find(c => c.id === variant.representativeCropId);
          const otherCrops = repCrop
            ? variant.crops.filter(c => c.id !== variant.representativeCropId)
            : variant.crops;
          const refImgSize = 36;
          const badgeSize = 11;
          const iconSize = 14;
          const isCollapsed = collapsedSections.has(sectionKey);
          const isDropTarget = (mode === 'crops-selected' || (mode === 'merge-source' && variantTarget.variantId !== mergeSourceVariantId)) && variantTarget.type !== 'pending';
          const isClickable = isDropTarget || isMergeSource || mode === 'idle';

          const refBoxStyle: React.CSSProperties = {
            width: refImgSize, height: refImgSize,
            flexShrink: 0, position: 'relative', overflow: 'visible', borderRadius: 3,
          };

          return (
            <div
              key={variant.id}
              style={{
                padding: '0 4px', marginBottom: 2,
                borderRadius: 'var(--radius-sm)',
                background: isVariantHighlighted ? 'var(--purple-subtle)' : 'transparent',
                boxShadow: isVariantHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
                transition: 'all 150ms ease',
              }}
              onClick={() => { if (mode === 'idle') onActiveVariantChange(activeVariantId === variant.id ? null : variant.id); }}
            >
              {/* Variant header — images + text */}
              <div
                onClick={(e) => {
                  if (isDropTarget || isMergeSource || mode === 'idle') {
                    e.stopPropagation();
                    onSectionClick(variantTarget);
                  }
                }}
                onMouseEnter={e => {
                  if (isDropTarget) { e.currentTarget.style.background = mode === 'merge-source' ? 'var(--positive-subtle)' : 'var(--purple-subtle)'; e.currentTarget.style.borderLeftColor = mode === 'merge-source' ? 'var(--positive)' : 'var(--purple)'; }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = isMergeSource ? 'var(--danger-subtle)' : 'transparent';
                  e.currentTarget.style.borderLeftColor = isMergeSource ? 'var(--danger)' : 'transparent';
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px 4px 0',
                  cursor: isClickable ? 'pointer' : 'default',
                  borderRadius: 'var(--radius-sm)',
                  borderLeft: isMergeSource ? '3px solid var(--danger)' : '3px solid transparent',
                  background: isMergeSource ? 'var(--danger-subtle)' : 'transparent',
                  transition: 'all 150ms ease', userSelect: 'none',
                }}
              >
                {/* Collapse toggle — 22px fixed column, same as renderSectionHeader */}
                <div style={{ width: 22, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {variant.crops.length > 0 && (
                    <div onClick={(e) => { e.stopPropagation(); setCollapsedSections(prev => { const n = new Set(prev); if (n.has(sectionKey)) n.delete(sectionKey); else n.add(sectionKey); return n; }); }} style={{ cursor: 'pointer', display: 'flex' }}>
                      {isCollapsed
                        ? <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                        : <KeyboardArrowDownIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                      }
                    </div>
                  )}
                </div>
                {/* F image */}
                {isConfirmed && variant.frontImageUrl ? (
                  <div style={{ ...refBoxStyle }}>
                    <div
                      style={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: 3, border: '1px solid var(--border)' }}
                      onMouseEnter={onImageHover ? (e) => onImageHover(e, variant.frontImageUrl!) : undefined}
                      onMouseMove={onImageMove ? (e) => onImageMove(e, variant.frontImageUrl!) : undefined}
                      onMouseLeave={onImageLeave}
                    >
                      <img src={getCachedImageUrl(variant.frontImageUrl)} alt="Front" style={{
                        width: '100%', height: '100%', objectFit: 'contain', background: '#000', display: 'block',
                      }} draggable={false} />
                    </div>
                    <CheckCircleIcon style={{
                      position: 'absolute', top: -4, right: -4, fontSize: badgeSize,
                      color: '#E8976C', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))',
                    }} />
                  </div>
                ) : (
                  <div style={{ ...refBoxStyle }}>
                    <div style={{
                      width: '100%', height: '100%', overflow: 'hidden', borderRadius: 3,
                      border: `1px dashed ${isBarcPending ? 'var(--warning)' : 'var(--border-hover)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'var(--surface)',
                    }}>
                      {isBarcPending
                        ? <HourglassEmptyIcon style={{ fontSize: iconSize, color: 'var(--warning)', opacity: 0.5 }} />
                        : <span style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: 'var(--text-tertiary)',
                          opacity: 0.45,
                          lineHeight: 1,
                        }}>F</span>
                      }
                    </div>
                    <CheckCircleIcon style={{
                      position: 'absolute', top: -4, right: -4, fontSize: badgeSize,
                      color: 'var(--text-tertiary)', opacity: 0.4,
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))',
                    }} />
                  </div>
                )}
                {/* Rep image */}
                {repCrop ? (() => {
                  const repMeta = buildCropZoomMeta(repCrop);
                  const isRepSelected = selectedCropIds.has(repCrop.id);
                  const isRepHighlighted = highlightedCropIds?.has(repCrop.id) ?? false;
                  const repBadgeConfig = getCropBadgeConfig(repCrop, 'front');
                  return <div style={{ ...refBoxStyle }}>
                    <div
                      onClick={(e) => { e.stopPropagation(); onCropClick(repCrop.id, e.shiftKey); }}
                      style={{
                        width: '100%', height: '100%', overflow: 'hidden', borderRadius: 3, cursor: 'pointer',
                        border: isRepSelected
                          ? '2px solid var(--blue)'
                          : isRepHighlighted
                            ? `3px solid ${highlightColor || 'var(--purple)'}`
                            : '1px solid var(--border)',
                        boxShadow: isRepHighlighted ? (highlightGlow || '0 0 12px rgba(188,140,255,0.6), 0 0 24px rgba(188,140,255,0.25)') : 'none',
                      }}
                      onMouseEnter={onImageHover ? (e) => onImageHover(e, repCrop.imageUrl, repMeta) : undefined}
                      onMouseMove={onImageMove ? (e) => onImageMove(e, repCrop.imageUrl, repMeta) : undefined}
                      onMouseLeave={onImageLeave}
                    >
                      <img src={getCachedImageUrl(repCrop.imageUrl)} alt="Rep" style={{
                        width: '100%', height: '100%', objectFit: 'contain', background: '#000', display: 'block',
                      }} draggable={false} />
                    </div>
                    {repBadgeConfig.leftMode && renderCropCornerBadge('left', {
                      mode: repBadgeConfig.leftMode,
                      label: repBadgeConfig.leftLabel,
                      background: repBadgeConfig.leftBackground,
                      border: repBadgeConfig.leftBorder,
                      color: repBadgeConfig.leftColor,
                    })}
                    {repBadgeConfig.rightMode && renderCropCornerBadge('right', {
                      mode: repBadgeConfig.rightMode,
                    })}
                    <StarIcon style={{
                      position: 'absolute', top: -4, right: -4, fontSize: badgeSize,
                      color: '#E8976C', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))',
                    }} />
                  </div>;
                })() : (
                  <div style={{
                    ...refBoxStyle, overflow: 'hidden',
                    border: '1px dashed var(--border-hover)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--surface)',
                  }}>
                    <StarIcon style={{ fontSize: iconSize, color: 'var(--text-tertiary)', opacity: 0.2 }} />
                  </div>
                )}
                {/* Variant text */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600,
                    color: isMergeSource ? 'var(--danger)' : 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {showReliableVariantLabel
                      ? (variantBarcodeInfo.productLabel || variantBarcodeInfo.barcode || 'Unknown')
                      : 'Unknown'}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {showReliableVariantLabel ? (
                      <>
                        {variantBarcodeInfo.barcode ? `${variantBarcodeInfo.barcode}  ` : ''}
                        {variant.id}
                      </>
                    ) : (
                      variant.id
                    )}
                  </span>
                </div>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-tertiary)', flexShrink: 0 }}>
                  {variant.crops.length}
                </span>
              </div>
              {/* Crops row */}
              {!isCollapsed && otherCrops.length > 0 && (
                <div onMouseEnter={() => preloadGroup('var-' + variant.id, variant.crops)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 8px 4px 0', alignItems: 'center' }}>
                  {sorted(otherCrops).map(crop => renderCrop(crop, 'front', variant.id, false))}
                </div>
              )}
            </div>
          );
        })}
        </div>

        {/* ── Group: Active Cartons (orange) ── */}
        <div style={{
          borderLeft: '2px solid rgba(232,151,108,0.35)', marginLeft: 4,
          background: 'rgba(232,151,108,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {(() => {
          const cartonSectionKey = `carton-${columnId}`;
          const cartonTarget: SectionTarget = { itemNumber: columnId, type: 'carton' };
          const isCartonHighlighted = checkHighlight(cartonSectionKey, cartonTarget);
          return (
            <div style={{
              padding: '0 4px', marginBottom: 2,
              borderRadius: 'var(--radius-sm)',
              background: isCartonHighlighted ? 'var(--purple-subtle)' : 'transparent',
              boxShadow: isCartonHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
              transition: 'all 150ms ease',
            }}>
              {renderSectionHeader('Active Cartons', upc.cartonCrops.length, cartonSectionKey, cartonTarget)}
              {!collapsedSections.has(cartonSectionKey) && (
                <div onMouseEnter={() => preloadGroup('carton-' + columnId, upc.cartonCrops)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 8px 4px 0' }}>
                  {sorted(upc.cartonCrops).map(crop => renderCrop(crop, 'carton'))}
                </div>
              )}
            </div>
          );
        })()}
        </div>

        {/* ── Group: Non-front (yellow) ── */}
        <div style={{
          borderLeft: '2px solid rgba(227,179,65,0.35)', marginLeft: 4,
          background: 'rgba(227,179,65,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {/* Non-front */}
        {(() => {
          const backImages = upc.variants
            .filter(v => v.backImageUrl)
            .map(v => ({ variantId: v.id, url: v.backImageUrl! }));
          const totalNonFront = upc.nonFrontCrops.length + backImages.length;
          const nfSectionKey = `nf-${columnId}`;
          const nfTarget: SectionTarget = { itemNumber: columnId, type: 'non-front' };
          const isNfHighlighted = checkHighlight(nfSectionKey, nfTarget);
          return (
            <div style={{
              padding: '0 4px', marginBottom: 2,
              borderRadius: 'var(--radius-sm)',
              background: isNfHighlighted ? 'var(--purple-subtle)' : 'transparent',
              boxShadow: isNfHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
              transition: 'all 150ms ease',
            }}>
              {renderSectionHeader('Non-front', totalNonFront, nfSectionKey, nfTarget)}
              {totalNonFront > 0 && !collapsedSections.has(nfSectionKey) && (
                <div style={{ padding: '2px 8px 4px 0' }}>
                  {backImages.length > 0 && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      padding: 3, marginBottom: 4,
                      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                      background: 'var(--elevated)',
                    }}>
                      {backImages.map(bi => (
                        <div
                          key={`back-${bi.variantId}`}
                          style={{ position: 'relative', overflow: 'hidden', borderRadius: 3, border: '1px solid var(--border)', flexShrink: 0 }}
                          onMouseEnter={onImageHover ? (e) => onImageHover(e, bi.url) : undefined}
                          onMouseMove={onImageMove ? (e) => onImageMove(e, bi.url) : undefined}
                          onMouseLeave={onImageLeave}
                        >
                          <img src={getCachedImageUrl(bi.url)} alt="Back" style={{
                            width: 40, height: 40, objectFit: 'contain', background: '#000', display: 'block',
                          }} draggable={false} />
                          {showTriangles && (
                            <div style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, borderTop: '10px solid var(--warning)', borderRight: '10px solid transparent', pointerEvents: 'none' }} />
                          )}
                          <span style={{
                            position: 'absolute', bottom: 1, left: 1, fontSize: 7, fontWeight: 700,
                            fontFamily: 'var(--font-mono)', color: '#fff',
                            background: 'rgba(0,0,0,0.6)', padding: '0 3px', borderRadius: 2,
                            lineHeight: 1.3,
                          }}>B</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {upc.nonFrontCrops.length > 0 && (
                    <div onMouseEnter={() => preloadGroup('nf-' + columnId, upc.nonFrontCrops)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {sorted(upc.nonFrontCrops).map(crop => renderCrop(crop, 'non-front'))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
        </div>

        {/* ── Group: Inactive (red) ── */}
        <div style={{
          borderLeft: '2px solid rgba(248,81,73,0.35)', marginLeft: 4,
          background: 'rgba(248,81,73,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {(() => {
          const inactiveCrops = upc.inactiveCrops || [];
          const inactiveSectionKey = `inactive-${columnId}`;
          return (
            <div style={{ padding: '0 4px', marginBottom: 2 }}>
              {renderSectionHeader('Inactive', inactiveCrops.length, inactiveSectionKey, { itemNumber: columnId, type: 'inactive' })}
              {inactiveCrops.length > 0 && !collapsedSections.has(inactiveSectionKey) && (
                <div onMouseEnter={() => preloadGroup('inactive-' + columnId, inactiveCrops)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 8px 4px 0' }}>
                  {sorted(inactiveCrops).map(crop => renderCrop(crop, 'front'))}
                </div>
              )}
            </div>
          );
        })()}
        </div>
      </div>
    </div>
  );
};
